import React, { useState, useEffect, useCallback } from 'react';
import { getBlacklist, getBlacklistStats, syncBlacklist, importBlacklistCsv, getEmailBlacklistStats } from '../api';

const PER_PAGE = 50;

function BlacklistTab() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, cbs: 0, alerts: 0, matched: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [emailStats, setEmailStats] = useState({ total: 0 });
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('type', typeFilter);
      params.set('limit', PER_PAGE);
      params.set('offset', (page - 1) * PER_PAGE);
      const [listRes, statsRes] = await Promise.all([
        getBlacklist(params.toString()),
        getBlacklistStats(),
      ]);
      if (listRes.success) { setEntries(listRes.data.entries); setTotal(listRes.data.total); }
      if (statsRes.success) setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [typeFilter, page]);

  useEffect(() => { fetchData(); getEmailBlacklistStats().then(r => { if (r.success) setEmailStats(r.data); }).catch(() => {}); }, [fetchData]);
  useEffect(() => { setPage(1); }, [typeFilter]);

  const handleSync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const res = await syncBlacklist();
      if (res.success) { setSyncResult(res.data); fetchData(); }
    } catch (err) { setSyncResult({ errors: [{ error: err.message }] }); }
    finally { setSyncing(false); }
  };

  const totalPages = Math.ceil(total / PER_PAGE);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-orange-500/5 rounded-xl border border-orange-500/20 p-5">
          <p className="text-xs text-slate-400 mb-1">Total Blacklisted</p>
          <p className="text-3xl font-bold text-orange-400">{stats.total}</p>
        </div>
        <div className="bg-red-500/5 rounded-xl border border-red-500/20 p-5">
          <p className="text-xs text-slate-400 mb-1">Chargebacks</p>
          <p className="text-3xl font-bold text-red-400">{stats.cbs}</p>
        </div>
        <div className="bg-yellow-500/5 rounded-xl border border-yellow-500/20 p-5">
          <p className="text-xs text-slate-400 mb-1">Alerts</p>
          <p className="text-3xl font-bold text-yellow-400">{stats.alerts}</p>
        </div>
        <div className="bg-purple-500/5 rounded-xl border border-purple-500/20 p-5">
          <p className="text-xs text-slate-400 mb-1">Matched to Clients</p>
          <p className="text-3xl font-bold text-purple-400">{stats.matched}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-white">Chargeback360 Blacklist</h3>
            <button onClick={handleSync} disabled={syncing}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-800 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors flex items-center space-x-2">
              {syncing ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Syncing...</span></> :
                <><span>🔄</span><span>Sync Blacklist</span></>}
            </button>
          </div>
          <div className="flex space-x-2">
            {['', 'CBs', 'Alerts'].map(f => (
              <button key={f} onClick={() => setTypeFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${typeFilter === f ? 'bg-orange-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}>
                {f || 'All'}
              </button>
            ))}
          </div>
        </div>

        {syncResult && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${syncResult.errors?.length > 0 && !syncResult.chargebacks_added ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-green-500/10 border border-green-500/30 text-green-400'}`}>
            {syncResult.chargebacks_added !== undefined ? (
              <div>
                <p>✅ Sync: <strong>{syncResult.chargebacks_added}</strong> CBs + <strong>{syncResult.alerts_added}</strong> Alerts added, <strong>{syncResult.total_matched}</strong> matched</p>
                <p className="text-xs mt-1">Period: {syncResult.period?.startDate} to {syncResult.period?.endDate} | Total: {syncResult.total_blacklisted}</p>
                {syncResult.errors?.length > 0 && <p className="text-xs mt-1 text-yellow-400">⚠️ {syncResult.errors.map(e => `${e.type}: ${e.error}`).join(', ')}</p>}
              </div>
            ) : <p>❌ {syncResult.errors?.[0]?.error || 'Sync failed'}</p>}
          </div>
        )}

      </div>

      {/* Email Blacklist Import */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white">📧 Email Blacklist (CSV Import)</h3>
            <p className="text-xs text-slate-400 mt-1">Import chargebacks CSV to blacklist clients by email. Emails in blacklist = bills skipped.</p>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-xs text-slate-400">Emails: <strong className="text-cyan-400">{emailStats.total}</strong> | Matched: <strong className="text-purple-400">{emailStats.matched || 0}</strong></span>
            <label className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium cursor-pointer transition-colors">
              {importing ? '⏳ Importing...' : '📄 Import CSV'}
              <input type="file" accept=".csv" className="hidden" disabled={importing} onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setImporting(true); setImportResult(null);
                try {
                  const text = await file.text();
                  const res = await importBlacklistCsv(text);
                  if (res.success) { setImportResult(res.data); getEmailBlacklistStats().then(r => { if (r.success) setEmailStats(r.data); }); }
                } catch (err) { setImportResult({ error: err.message }); }
                finally { setImporting(false); e.target.value = ''; }
              }} />
            </label>
          </div>
        </div>
        {importResult && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${importResult.error ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-green-500/10 border border-green-500/30 text-green-400'}`}>
            {importResult.error ? <p>❌ {importResult.error}</p> :
              <p>✅ Import: <strong>{importResult.added}</strong> added, <strong>{importResult.skipped}</strong> already existed, <strong>{importResult.errors}</strong> errors | Total: {importResult.total}</p>}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-700/50">
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Type</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Card</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Txn Date</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Amount</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Processor</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Reason</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Matched</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {entries.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                  {stats.total === 0 ? 'No blacklist entries. Click "Sync Blacklist" to fetch from Chargeback360.' : 'No entries for this filter.'}
                </td></tr>
              ) : entries.map(e => (
                <tr key={e.id} className="hover:bg-slate-700/30">
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      e.request_type === 'CBs' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'
                    }`}>
                      {e.record_type || e.request_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-300 font-mono text-xs">{e.cc_first6}****{e.cc_last4}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{e.transaction_date || '—'}</td>
                  <td className="px-3 py-2 text-slate-300 text-xs">${e.order_amount || e.amount || '—'}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{e.merc_iso_processor || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="text-orange-400">{e.status || '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs max-w-40 truncate" title={e.reason_description || e.cb_reason_code}>
                    {e.cb_reason_code || e.reason_description || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {e.matched_client_id ? (
                      <span className="text-purple-400">Client #{e.matched_client_id}</span>
                    ) : <span className="text-slate-500">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
            <p className="text-xs text-slate-400">Showing {(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE,total)} of {total}</p>
            <div className="flex items-center space-x-2">
              <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page<=1} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white rounded text-xs">← Prev</button>
              <span className="text-xs text-slate-400">Page {page}/{totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page>=totalPages} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white rounded text-xs">Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BlacklistTab;
